/**
 * Making authored HTML survive dark mode — Step 33, after Kai's second
 * report ("the whole background is still white, but it doesn't match the
 * admin end").
 *
 * The problem: imported articles were authored against a white page, so they
 * carry inline colours — `color: rgb(0,0,0)` on body text, `#C82613` on a
 * brand name, `background-color: #fff` on wrapper divs. Inline styles beat
 * every stylesheet, so on a dark surface the article is black-on-black.
 *
 * Three approaches, and why this is the one:
 *   - Render on a white sheet: works, but a white slab in a dark app looks
 *     wrong next to everything else (Kai's report).
 *   - Strip all colours: the signature templates exist to show EXACT brand
 *     colours; stripping them makes the article lie about what to paste
 *     into Outlook.
 *   - Adapt the colours to the theme — what good dark-mode mail clients do,
 *     and what this does: keep every hue, raise only the lightness of
 *     colours too dark to read on a dark surface, and drop backgrounds that
 *     merely assumed a white page.
 *
 * Light mode is left completely untouched: the content was authored for it.
 */

interface Hsl {
  h: number;
  s: number;
  l: number;
  a: number;
}

const NAMED: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  gray: '#808080',
  grey: '#808080',
  darkgray: '#a9a9a9',
  darkgrey: '#a9a9a9',
  silver: '#c0c0c0',
  red: '#ff0000',
  blue: '#0000ff',
  green: '#008000',
  navy: '#000080',
  maroon: '#800000',
  transparent: 'transparent',
  inherit: 'inherit',
};

function parseColour(input: string): Hsl | null {
  const raw = input.trim().toLowerCase();
  const named = NAMED[raw];
  if (named === 'transparent' || named === 'inherit') return null;
  const value = named ?? raw;

  let r: number,
    g: number,
    b: number,
    a = 1;
  const hex = /^#([0-9a-f]{3,8})$/.exec(value);
  if (hex) {
    let h = hex[1]!;
    if (h.length === 3)
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    if (h.length !== 6 && h.length !== 8) return null;
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
    if (h.length === 8) a = parseInt(h.slice(6, 8), 16) / 255;
  } else if (/^hsla?\(/.test(value)) {
    // Authored articles can carry hsl() too, and our own adapted output is
    // hsl() — without this, re-reading an adapted colour silently fails.
    const m = /^hsla?\(([^)]+)\)$/.exec(value);
    if (!m) return null;
    const p = m[1]!.split(/[,\s/]+/).filter(Boolean);
    const hh = parseFloat(p[0] ?? '');
    const ss = parseFloat((p[1] ?? '').replace('%', ''));
    const ll = parseFloat((p[2] ?? '').replace('%', ''));
    if ([hh, ss, ll].some((n) => Number.isNaN(n))) return null;
    const alpha = p[3] !== undefined ? parseFloat(p[3].replace('%', '')) : 1;
    return { h: hh, s: ss, l: ll, a: Number.isNaN(alpha) ? 1 : alpha > 1 ? alpha / 100 : alpha };
  } else {
    const rgb = /^rgba?\(([^)]+)\)$/.exec(value);
    if (!rgb) return null;
    const parts = rgb[1]!
      .split(/[,\s/]+/)
      .filter(Boolean)
      .map((p) => parseFloat(p));
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
    [r, g, b] = parts as [number, number, number];
    if (parts.length > 3) a = parts[3]!;
  }

  const rn = r / 255,
    gn = g / 255,
    bn = b / 255;
  const max = Math.max(rn, gn, bn),
    min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
    else if (max === gn) h = ((bn - rn) / d + 2) * 60;
    else h = ((rn - gn) / d + 4) * 60;
  }
  return { h, s: s * 100, l: l * 100, a };
}

/**
 * Readability is decided by LUMINANCE, not by HSL lightness — a first cut
 * used lightness and would have washed out `#FF9900`, which sits at 50%
 * lightness but is plainly readable on a dark page because a saturated
 * orange carries far more luminance than a grey of the same "lightness".
 * The unit test that caught it is in apps/api/tests/articleTheme.test.ts.
 */
function channelToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance from HSL, via RGB. */
function luminanceOf(h: number, s: number, l: number): number {
  const [r, g, b] = hslToRgb(h, s, l);
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;
  const [r1, g1, b1] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [(r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255];
}

/** The dark card's own luminance (roughly hsl(222 47% 11%)), and the 4.5:1
 *  contrast the rest of the product already holds itself to. */
const SURFACE_LUMINANCE = 0.0075;
const MIN_LUMINANCE = 4.5 * (SURFACE_LUMINANCE + 0.05) - 0.05;

/** Contrast of a CSS colour against the dark surface, for tests and for any
 *  future "is this readable?" check. Exposed because asserting CONTRAST is
 *  the only assertion that means anything — an early test asserted HSL
 *  lightness instead and had to be rewritten when the model changed. */
export function contrastOnDark(value: string): number | null {
  const c = parseColour(value);
  if (!c) return null;
  return (luminanceOf(c.h, c.s, c.l) + 0.05) / (SURFACE_LUMINANCE + 0.05);
}

export function adaptColour(value: string): string | null {
  const c = parseColour(value);
  if (!c) return null;
  if (luminanceOf(c.h, c.s, c.l) >= MIN_LUMINANCE) return null; // readable as authored — leave it alone
  // Keep hue and saturation — the brand red stays red, the navy stays navy —
  // and raise ONLY lightness, which is the part that assumed a white page.
  // Step up until it clears the bar rather than guessing a formula: hue and
  // saturation change how much lift a colour needs, sometimes by a lot.
  let l = c.l;
  while (l < 92 && luminanceOf(c.h, c.s, l) < MIN_LUMINANCE) l += 2;
  return `hsl(${Math.round(c.h)} ${Math.round(c.s)}% ${Math.round(l)}%${c.a < 1 ? ` / ${c.a}` : ''})`;
}

/** True when a background merely assumes a white page, so dropping it lets
 *  our themed surface show through. A deliberate coloured highlight is kept. */
function isPageAssumption(value: string): boolean {
  const c = parseColour(value);
  if (!c) return true; // transparent / inherit — nothing to keep
  return c.l >= 85 && c.s <= 15;
}

/**
 * Rewrite an article's inline colours for a dark surface. Returns the HTML
 * unchanged when `dark` is false.
 *
 * Parsing with DOMParser is safe here because the HTML was already sanitised
 * ON WRITE (lib/sanitize.ts) — this never introduces markup, it only edits
 * style declarations that are already present and already allowlisted.
 */
export function adaptArticleHtml(html: string, dark: boolean): string {
  if (!dark || !html) return html;
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html;

  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[style]'))) {
    const style = el.getAttribute('style') ?? '';
    const kept: string[] = [];
    for (const decl of style.split(';')) {
      const idx = decl.indexOf(':');
      if (idx < 0) continue;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).trim();
      if (!prop || !value) continue;

      if (prop === 'color') {
        const adapted = adaptColour(value);
        kept.push(`color: ${adapted ?? value}`);
        continue;
      }
      if (prop === 'background-color' || prop === 'background') {
        // A white wrapper is the single biggest cause of the "white box in a
        // dark app" Kai saw — drop it and let the themed card show through.
        if (isPageAssumption(value)) continue;
        kept.push(`${prop}: ${value}`);
        continue;
      }
      if (prop === 'border' || prop.startsWith('border-')) {
        // Hairline borders drawn for a white page turn into bright lines on
        // dark. Keep the geometry, let CSS colour it.
        kept.push(`${prop}: ${value.replace(/#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)/g, 'currentColor')}`);
        continue;
      }
      kept.push(`${prop}: ${value}`);
    }
    if (kept.length) el.setAttribute('style', kept.join('; '));
    else el.removeAttribute('style');
  }
  return root.innerHTML;
}
